import React, { useState } from "react";
import { Calendar } from "lucide-react";
import { Container, Button } from "@/components/primitives";
import { Reveal } from "@/components/Reveal";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { PageHero, CTASection, ReviewsSection } from "@/components/sections";
import { useBooking } from "@/lib/booking";
import { useSeo } from "@/lib/seo";
import { gallery, galleryFilters, services } from "@/data/site";
import { cn } from "@/lib/utils";

export default function SmileGallery() {
  const [filter, setFilter] = useState("All");
  const { openBooking } = useBooking();

  useSeo({
    title: "Smile Gallery — Before & After Transformations | Meridian Dental, NYC",
    description: "See real before & after smile transformations — porcelain veneers, Invisalign, whitening, implants & makeovers — at Meridian Dental Studio in Hell's Kitchen, Manhattan.",
    path: "/smile-gallery",
  });

  const items = filter === "All" ? gallery : gallery.filter((g) => g.category === filter || (filter === "Orthodontics" && g.category === "Orthodontics"));

  return (
    <>
      <PageHero
        eyebrow="Smile Gallery"
        title="Proof you can drag your finger across."
        intro="Every transformation below was designed and delivered in our Hell's Kitchen studio. Drag the slider to reveal the before, then book the treatment that inspires you."
      />

      <section className="pb-8">
        <Container>
          <div className="flex flex-wrap gap-2.5" data-testid="gallery-filters">
            {galleryFilters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`gallery-filter-${f.toLowerCase()}`}
                className={cn(
                  "rounded-full border px-5 py-2.5 text-sm transition-colors",
                  filter === f
                    ? "border-[var(--primary)] bg-[var(--primary)] text-[var(--on-primary)]"
                    : "border-[var(--border)] text-[var(--fg)]/80 hover:border-[var(--primary)] hover:text-[var(--primary)]"
                )}
              >
                {f}
              </button>
            ))}
          </div>
        </Container>
      </section>

      <section className="pb-24 sm:pb-32">
        <Container>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
            {items.map((g, i) => {
              const svc = services.find((s) => s.slug === g.service);
              return (
                <Reveal key={g.id} delay={(i % 3) * 0.08}>
                  <div className="flex flex-col">
                    <BeforeAfterSlider before={g.before} after={g.after} alt={g.title} />
                    <div className="mt-5 flex items-start justify-between gap-4">
                      <div>
                        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--primary)]">{g.category}</span>
                        <h3 className="mt-1 font-heading text-2xl font-normal tracking-tight text-[var(--fg)]">{g.title}</h3>
                        <p className="mt-1 text-sm text-[var(--muted-fg)]">{g.caption}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="mt-4 w-full"
                      onClick={() => openBooking(svc ? svc.name : "")}
                      data-testid={`gallery-book-${g.id}`}
                    >
                      <Calendar size={15} /> Book This Treatment
                    </Button>
                  </div>
                </Reveal>
              );
            })}
          </div>
          {items.length === 0 && (
            <p className="py-16 text-center text-[var(--muted-fg)]">No transformations in this category yet — check back soon.</p>
          )}
        </Container>
      </section>

      <ReviewsSection />
      <CTASection eyebrow="Your turn" title="Ready to design your new smile?" />
    </>
  );
}
